/**
 * Community API functions.
 *
 * Maps to backend scenario-service endpoints:
 *   POST   /api/communities          - Create community
 *   GET    /api/communities          - List communities
 *   GET    /api/communities/:id      - Get full community (nested)
 *   PUT    /api/communities/:id      - Update community
 *   DELETE /api/communities/:id      - Delete community
 *   POST   /api/communities/import   - Import community from JSON
 */

import client from './client';

/**
 * List all communities (paginated).
 * @param {number} [skip=0]
 * @param {number} [limit=100]
 * @returns {Promise<Array>} Array of community objects
 */
export const getCommunities = async (skip = 0, limit = 100) => {
  return client.get('/api/communities', { params: { skip, limit } });
};

/**
 * Get a single community with full nested data (buildings, assets, datasets).
 * @param {string} communityId - UUID
 * @returns {Promise<Object>} Full community object
 */
export const getCommunity = async (communityId) => {
  return client.get(`/api/communities/${communityId}`);
};

/**
 * Create a new community.
 * @param {Object} data - { name, timezone, currency, metadata: { country, location, description } }
 * @returns {Promise<Object>} Created community
 */
export const createCommunity = async (data) => {
  return client.post('/api/communities', data);
};

/**
 * Update a community (partial).
 * @param {string} communityId - UUID
 * @param {Object} data - Fields to update
 * @returns {Promise<Object>} Updated community
 */
export const updateCommunity = async (communityId, data) => {
  return client.put(`/api/communities/${communityId}`, data);
};

/**
 * Delete a community (cascade-deletes all children).
 * @param {string} communityId - UUID
 * @returns {Promise<void>}
 */
export const deleteCommunity = async (communityId) => {
  return client.delete(`/api/communities/${communityId}`);
};

/**
 * Import a full community from the JSON export format.
 * @param {Object} data - CommunityExport schema object
 * @returns {Promise<Object>} Created full community
 */
export const importCommunity = async (data) => {
  return client.post('/api/communities/import', data);
};
